import { EPermissions } from '@/types/permissions.types';
import useHasPermission from '@/utils/authentication/use-has-permission';
import { Activity, useCallback, useRef, useState } from 'react';
import styles from './project-image-carousel.module.css';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import UploadLarge from '@ui/uploads/upload-large';
import OptionalComponent from '@ui/optional-component/optional-component';
import ProjectColorText from '@ui/text/text/project-color-text';
import Container from '@ui/container/container';
import {
  GetUploadEndpoint,
  getUploadFilename,
  SetItemContentType,
  UploadFileToProvider,
} from '@ui/uploads/validations';
import { isObject } from 'underscore';
import handleImageUploadErrors from '@ui/image-upload/handle-image-upload-errors';

type Props = {
  disabled?: boolean;
  isSingleSession?: boolean;
  onUpload: (key: string, url: string) => Promise<void>;
  onDelete: (key: string) => void;
  images: {
    projectImage: string | null | undefined;
    registrationImage: string | null | undefined;
    sessionOrBannerImage: string | null | undefined;
  };
};

const classMap = [styles.first, styles.second, styles.third];
const positions = [
  [0, 1, 2],
  [1, 2, 0],
  [2, 0, 1],
];

const MAX_SIZE = 1024 * 1024 * 10; // 10 MB
type ColorState = 'light' | 'dark' | 'unset';
type ColorStateArray = [ColorState, ColorState, ColorState];
const defaultColorState: ColorStateArray = ['dark', 'dark', 'dark'];

export default function ProjectImageCarousel({ isSingleSession = true, onUpload, onDelete, images, disabled }: Props) {
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUpLoading] = useState(false);
  const [currentImage, setCurrentImage] = useState<number>(0);
  const [action, setAction] = useState<'toNext' | 'toPrev'>('toNext');
  const [imageColorState, setImageColorState] = useState<ColorStateArray>(defaultColorState);
  const { hasPermission } = useHasPermission({ allowedRoles: [EPermissions.GroupAdmin, EPermissions.Editor] });
  const _disabled = disabled || !hasPermission;

  const next = () => {
    setCurrentImage((prev) => {
      const next = (prev - 1) % 3;
      if (next < 0) {
        return 2;
      }

      return next;
    });
    setAction('toNext');
  };

  const prev = () => {
    setCurrentImage((prev) => (prev + 1) % 3);
    setAction('toPrev');
  };

  const getClass = (index: number) => {
    return classMap[positions[currentImage][index]];
  };

  const handleFile = (key: string) => async (file: File | FileList) => {
    try {
      if (file instanceof File) {
        setUpLoading(true);

        const endpoint = await GetUploadEndpoint({
          filename: getUploadFilename(file.name),
        });

        if (isObject(endpoint) && endpoint.url) {
          const finalUrl = await UploadFileToProvider(
            {
              url: endpoint.url,
              fields: endpoint.fields,
              cdnHost: endpoint?.cdnHost,
            },
            file
          );

          SetItemContentType({ filename: finalUrl, contentType: file.type }).catch(console.error);

          if (finalUrl) {
            await onUpload(key, finalUrl);
          }
        }
      }
    } catch (error) {
      console.error(error);
      handleImageUploadErrors(error);
    } finally {
      setUpLoading(false);
    }
  };

  const handleImageColorStateSet = useCallback((index: number) => {
    return (color: 'light' | 'dark') => {
      setImageColorState((state) => {
        // can over-fire if we always update,
        // so check if change then update.
        if (state[index] !== color) {
          state[index] = color;
          return [...state];
        }
        return state;
      });
    };
  }, []);

  const handleDelete = (key: string) => () => {
    onDelete(key);
  };

  const getButtonClass = (index: number) => {
    switch (index) {
      case 0: {
        return images.projectImage ? styles[`background-is-${imageColorState[index]}`] : 'unset';
      }
      case 1: {
        return images.sessionOrBannerImage ? styles[`background-is-${imageColorState[index]}`] : 'unset';
      }
      case 2: {
        return images.registrationImage ? styles[`background-is-${imageColorState[index]}`] : 'unset';
      }
      default:
        return '';
    }
  };

  return (
    <div className={`${styles.projectImageCarouselContainer}`} data-component="ProjectImageCarousel">
      <div className={styles.carouselCards}>
        <div
          className={`${styles.carouselCard} ${getClass(0)} ${styles[action]} ${images.projectImage ? styles.hasImage : styles.empty}`}
          inert={currentImage !== 0} // prevents other carousel item buttons from being accessed
        >
          <UploadLarge
            displayImageUrl={images.projectImage}
            editable={hasPermission}
            deletable={hasPermission}
            disabled={_disabled}
            uploading={uploading}
            ref={uploadInputRef}
            noGradients={true}
            isFocusable={!images.projectImage}
            maxSizeInBytes={MAX_SIZE}
            standard
            CustomTitle={
              <div className="flex-column jc-center ai-center height-100-percent width-100-percent margin-auto overlow-hidden radius-10 padding-8">
                <ProjectColorText contrastAgainstAppBackground className="L2">
                  <Container className="flex-column ai-center jc-center gap-6">
                    <Icon name={ICONS.IMAGE_UPLOAD} />
                    <OptionalComponent display={hasPermission}>
                      <div>Upload image</div>
                    </OptionalComponent>
                  </Container>
                </ProjectColorText>
              </div>
            }
            EditActionsPreHoverContent={({ hasValidContrast }) => (
              <Activity mode={currentImage !== 0 ? 'hidden' : 'visible'}>
                {/* do not use Text or Container becaue we have very specific class styles here based on contrast */}
                <div
                  className={`S3 ${styles.cardLabel} ${images.projectImage ? '' : styles['no-img']} ${hasValidContrast?.hasValidContrast ? '' : styles['invalid-contrast']} ${styles[`background-is-${hasValidContrast.backgroundMode}`]}`}
                >
                  Project image
                </div>
              </Activity>
            )}
            onFile={handleFile('projectImage')}
            onDelete={handleDelete('projectImage')}
            allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png', 'image/gif']}
            setImageColorState={handleImageColorStateSet(0)}
          />
        </div>
        <div
          className={`${styles.carouselCard} ${getClass(1)} ${styles[action]} ${images.registrationImage ? styles.hasImage : styles.empty}`}
          inert={currentImage !== 2} // not exactly sure why this has to be 2 instead if 1?
        >
          <UploadLarge
            displayImageUrl={images.registrationImage}
            editable={hasPermission}
            deletable={hasPermission}
            disabled={_disabled}
            uploading={uploading}
            ref={uploadInputRef}
            noGradients={true}
            isFocusable={!images.registrationImage}
            maxSizeInBytes={MAX_SIZE}
            standard
            CustomTitle={
              <div className="flex-column jc-center ai-center height-100-percent width-fit-content margin-auto overlow-hidden radius-10 padding-8">
                <ProjectColorText contrastAgainstAppBackground className="L2">
                  <Container className="flex-column ai-center jc-center gap-6">
                    <Icon name={ICONS.IMAGE_UPLOAD} />
                    <OptionalComponent display={hasPermission}>
                      <div>Upload image</div>
                    </OptionalComponent>
                  </Container>
                </ProjectColorText>
              </div>
            }
            EditActionsPreHoverContent={({ hasValidContrast }) => (
              <Activity mode={currentImage !== 2 ? 'hidden' : 'visible'}>
                {/* do not use Text or Container becaue we have very specific class styles here based on contrast */}
                <div
                  className={`S3 ${styles.cardLabel} ${images.registrationImage ? '' : styles['no-img']} ${hasValidContrast?.hasValidContrast ? '' : styles['invalid-contrast']} ${styles[`background-is-${hasValidContrast.backgroundMode}`]}`}
                >
                  Registration image
                </div>
              </Activity>
            )}
            onFile={handleFile('registrationImage')}
            onDelete={handleDelete('registrationImage')}
            allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png', 'image/gif']}
            setImageColorState={handleImageColorStateSet(1)}
          />
        </div>
        <div
          className={`${styles.carouselCard} ${getClass(2)} ${styles[action]} ${images.sessionOrBannerImage ? styles.hasImage : styles.empty}`}
          inert={currentImage !== 1} // not exactly sure why this has to be 1 instead if 2?
        >
          <UploadLarge
            displayImageUrl={images.sessionOrBannerImage}
            editable={hasPermission}
            deletable={hasPermission}
            disabled={_disabled}
            uploading={uploading}
            ref={uploadInputRef}
            noGradients={true}
            isFocusable={!images.sessionOrBannerImage}
            maxSizeInBytes={MAX_SIZE}
            standard
            CustomTitle={
              <div className="flex-column jc-center ai-center height-100-percent width-fit-content margin-auto overlow-hidden radius-10 padding-8">
                <ProjectColorText contrastAgainstAppBackground className="L2">
                  <Container className="flex-column ai-center jc-center gap-6">
                    <Icon name={ICONS.IMAGE_UPLOAD} />
                    <OptionalComponent display={hasPermission}>
                      <div>Upload image</div>
                    </OptionalComponent>
                  </Container>
                </ProjectColorText>
              </div>
            }
            EditActionsPreHoverContent={({ hasValidContrast }) => (
              <Activity mode={currentImage !== 1 ? 'hidden' : 'visible'}>
                {/* do not use Text or Container becaue we have very specific class styles here based on contrast */}
                <div
                  className={`S3 ${styles.cardLabel} ${images.sessionOrBannerImage ? '' : styles['no-img']} ${hasValidContrast?.hasValidContrast ? '' : styles['invalid-contrast']} ${styles[`background-is-${hasValidContrast.backgroundMode}`]}`}
                >
                  {isSingleSession ? 'Session image' : 'Banner image'}
                </div>
              </Activity>
            )}
            onFile={handleFile('sessionOrBannerImage')}
            onDelete={handleDelete('sessionOrBannerImage')}
            allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png', 'image/gif']}
            setImageColorState={handleImageColorStateSet(2)}
          />
        </div>
        <div className={`${styles.carouselButton} ${styles.next} ${getButtonClass(currentImage)}`}>
          <Button
            backgroundColor="rgba(var(--gray-0-rgb), 0.05)"
            hoverBackgroundColor="rgba(var(--gray-0-rgb), 0.3)"
            height="32px"
            width="32px"
            padding="0"
            borderRadius="100%"
            border="1px solid rgba(var(--gray-0-rgb), 0.05)"
            onClick={prev}
          >
            <Icon name={ICONS.ARROW_LEFT_3} size={12} />
          </Button>
        </div>
        <div className={`${styles.carouselButton} ${styles.prev} ${getButtonClass(currentImage)}`}>
          <Button
            backgroundColor="rgba(var(--gray-0-rgb), 0.05)"
            hoverBackgroundColor="rgba(var(--gray-0-rgb), 0.3)"
            height="32px"
            width="32px"
            padding="0"
            borderRadius="100%"
            border="1px solid rgba(var(--gray-true-dark-0-rgb), 0.05)"
            onClick={next}
          >
            <Icon name={ICONS.ARROW_RIGHT_3} size={12} />
          </Button>
        </div>
      </div>
    </div>
  );
}
